import { useEffect, useState } from 'react';
import { formatCents, type SaleDetail, type SaleListRow } from '@equinox/shared';
import { useI18n } from '../../i18n/I18nContext.js';
import { salesApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import { Banner } from '../../components/Banner.js';
import { Modal } from '../../components/Modal.js';
import { SortableColumnHeader } from '../../components/table/SortableColumnHeader.js';
import { daysAgoISO, formatDateTime, todayISO } from '../../lib/format.js';

const PAGE_SIZE = 50;
type SortKey = 'soldAt' | 'totalCents';
type Translate = ReturnType<typeof useI18n>['t'];

function methodLabel(t: Translate, m: string): string {
  return m === 'cash' ? t('reports.methodCash') : t('reports.methodCard');
}

function StatusBadge({ status, t }: { status: 'completed' | 'voided'; t: Translate }) {
  return status === 'voided' ? (
    <span className="badge badge--warn">{t('sales.statusVoided')}</span>
  ) : (
    <span className="badge badge--ok">{t('sales.statusCompleted')}</span>
  );
}

function SaleDetailView({ saleId, folio, onClose }: { saleId: string; folio: string; onClose(): void }) {
  const { t } = useI18n();
  const [sale, setSale] = useState<SaleDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    salesApi
      .get(saleId)
      .then((s) => {
        if (!cancelled) setSale(s);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : t('common.error'));
      });
    return () => {
      cancelled = true;
    };
  }, [saleId]);

  return (
    <Modal title={t('sales.detailTitle', { folio })} onClose={onClose} wide>
      {error && <Banner kind="danger">{error}</Banner>}
      {!sale && !error && <p>{t('common.loading')}</p>}
      {sale && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'center' }}>
            <StatusBadge status={sale.status} t={t} />
            <span>
              {t('sales.soldAt')}: {formatDateTime(sale.soldAt)}
            </span>
            <span>
              {t('sales.staff')}: {sale.staffName}
            </span>
            <span>
              {t('sales.terminal')}: {sale.terminalName}
            </span>
          </div>
          <p>
            {t('sales.receivedAt')}: {formatDateTime(sale.receivedAt)}
          </p>
          {sale.status === 'voided' && (
            <Banner kind="warn">
              {sale.voidedAt ? `${t('sales.voidedAt')}: ${formatDateTime(sale.voidedAt)}. ` : ''}
              {sale.voidReason ? t('sales.voidReason', { reason: sale.voidReason }) : ''}
            </Banner>
          )}

          <h3>{t('sales.detailLines')}</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('products.name')}</th>
                  <th className="numeric">{t('sales.qty')}</th>
                  <th className="numeric">{t('sales.unitPrice')}</th>
                  <th className="numeric">{t('sales.lineDiscount')}</th>
                  <th className="numeric">{t('sales.total')}</th>
                </tr>
              </thead>
              <tbody>
                {sale.lines.map((l) => (
                  <tr key={l.lineNo}>
                    <td>
                      {l.name}
                      {l.options.length > 0 && (
                        <div>
                          <small>
                            {l.options.map((o) => o.name).join(', ')}
                          </small>
                        </div>
                      )}
                      {l.discountReason && (
                        <div>
                          <small>{t('sales.discountReason', { reason: l.discountReason })}</small>
                          {l.discountNote && <small> — {l.discountNote}</small>}
                        </div>
                      )}
                    </td>
                    <td className="numeric">{l.qty}</td>
                    <td className="numeric">{formatCents(l.unitPriceCents)}</td>
                    <td className="numeric">{l.lineDiscountCents > 0 ? formatCents(l.lineDiscountCents) : ''}</td>
                    <td className="numeric">{formatCents(l.totalCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>{t('sales.detailPayments')}</h3>
          <ul style={{ margin: 0, paddingLeft: 'var(--space-4)' }}>
            {sale.payments.map((p) => (
              <li key={p.seq}>
                <strong>{methodLabel(t, p.method)}</strong> <span className="numeric">{formatCents(p.amountCents)}</span>
                {p.method === 'cash' && p.tenderedCents != null && (
                  <small>
                    {' '}
                    · {t('sales.tendered')} {formatCents(p.tenderedCents)} · {t('sales.change')} {formatCents(p.changeCents ?? 0)}
                  </small>
                )}
                {p.method === 'card' && (
                  <small>
                    {p.cardType ? ` · ${t(p.cardType === 'credit' ? 'sales.cardType.credit' : 'sales.cardType.debit')}` : ''}
                    {p.cardBrand ? ` · ${p.cardBrand}` : ''}
                    {p.authLast4 ? ` · ${t('sales.authLast4', { last4: p.authLast4 })}` : ''}
                    {p.processor ? ` · ${t('sales.processor')} ${p.processor}` : ''}
                  </small>
                )}
                {p.refundStatus && (
                  <small>
                    {' '}
                    · {t('sales.refund', { status: p.refundStatus })}
                    {p.refundError ? ` (${p.refundError})` : ''}
                  </small>
                )}
              </li>
            ))}
          </ul>

          <dl style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 'var(--space-2)', margin: 0, maxWidth: 320, marginLeft: 'auto' }}>
            <dt>{t('sales.subtotal')}</dt>
            <dd className="numeric" style={{ margin: 0, textAlign: 'right' }}>{formatCents(sale.subtotalCents)}</dd>
            <dt>{t('sales.discountTotal')}</dt>
            <dd className="numeric" style={{ margin: 0, textAlign: 'right' }}>{formatCents(sale.discountCents)}</dd>
            <dt><strong>{t('sales.grandTotal')}</strong></dt>
            <dd className="numeric" style={{ margin: 0, textAlign: 'right' }}><strong>{formatCents(sale.totalCents)}</strong></dd>
          </dl>

          <div className="modal__actions">
            <button type="button" className="btn btn--secondary" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export function SalesPage() {
  const { t } = useI18n();
  const [from, setFrom] = useState(daysAgoISO(30));
  const [to, setTo] = useState(todayISO());
  const [status, setStatus] = useState<'' | 'completed' | 'voided'>('');
  const [method, setMethod] = useState<'' | 'cash' | 'card'>('');
  const [folio, setFolio] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('soldAt');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<SaleListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<SaleListRow | null>(null);

  // Any filter or sort change goes back to the first page.
  useEffect(() => setOffset(0), [from, to, status, method, folio, sortKey, dir]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    salesApi
      .list({
        from,
        to,
        status: status || undefined,
        method: method || undefined,
        folio: folio || undefined,
        sort: sortKey,
        dir,
        limit: PAGE_SIZE,
        offset,
      })
      .then((r) => {
        if (cancelled) return;
        setRows(r.sales);
        setTotal(r.total);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : t('common.error'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to, status, method, folio, sortKey, dir, offset]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setDir('desc');
    }
  }
  const ariaSortFor = (key: SortKey): 'ascending' | 'descending' | 'none' =>
    key !== sortKey ? 'none' : dir === 'asc' ? 'ascending' : 'descending';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="page-header">
        <div>
          <h1>{t('sales.title')}</h1>
          <p>{t('sales.subtitle')}</p>
        </div>
      </div>

      <div className="card toolbar">
        <label htmlFor="sales-from">
          {t('reports.from')}
          <input id="sales-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label htmlFor="sales-to">
          {t('reports.to')}
          <input id="sales-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label htmlFor="sales-status">
          {t('sales.status')}
          <select id="sales-status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="">{t('common.all')}</option>
            <option value="completed">{t('sales.statusCompleted')}</option>
            <option value="voided">{t('sales.statusVoided')}</option>
          </select>
        </label>
        <label htmlFor="sales-method">
          {t('reports.filterMethod')}
          <select id="sales-method" value={method} onChange={(e) => setMethod(e.target.value as typeof method)}>
            <option value="">{t('common.all')}</option>
            <option value="cash">{t('reports.methodCash')}</option>
            <option value="card">{t('reports.methodCard')}</option>
          </select>
        </label>
        <label htmlFor="sales-folio">
          {t('sales.folio')}
          <input
            id="sales-folio"
            type="search"
            value={folio}
            placeholder={t('sales.searchFolio')}
            onChange={(e) => setFolio(e.target.value)}
          />
        </label>
      </div>

      {error && <Banner kind="danger">{error}</Banner>}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p>{t('sales.noData')}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('sales.folio')}</th>
                <SortableColumnHeader label={t('sales.dateTime')} ariaSort={ariaSortFor('soldAt')} onSort={() => toggleSort('soldAt')} />
                <th>{t('sales.staff')}</th>
                <th>{t('sales.terminal')}</th>
                <th className="numeric">{t('sales.items')}</th>
                <th>{t('sales.methods')}</th>
                <SortableColumnHeader
                  label={t('sales.total')}
                  align="right"
                  ariaSort={ariaSortFor('totalCents')}
                  onSort={() => toggleSort('totalCents')}
                />
                <th>{t('sales.status')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.saleId}>
                  <td>
                    <button type="button" className="btn btn--ghost btn--sm" aria-label={t('sales.open', { folio: r.folio })} onClick={() => setOpen(r)}>
                      {r.folio}
                    </button>
                  </td>
                  <td>{formatDateTime(r.soldAt)}</td>
                  <td>{r.staffName}</td>
                  <td>{r.terminalName}</td>
                  <td className="numeric">{r.itemCount}</td>
                  <td>{r.methods.map((m) => methodLabel(t, m)).join(' + ')}</td>
                  <td className="numeric">{formatCents(r.totalCents)}</td>
                  <td>
                    <StatusBadge status={r.status} t={t} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > 0 && (
        <div className="card toolbar">
          <span>
            {t('sales.range', { from: String(offset + 1), to: String(Math.min(offset + PAGE_SIZE, total)), total: String(total) })}
          </span>
          <button type="button" className="btn btn--secondary" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            {t('sales.prev')}
          </button>
          <button type="button" className="btn btn--secondary" disabled={offset + PAGE_SIZE >= total || loading} onClick={() => setOffset(offset + PAGE_SIZE)}>
            {t('sales.next')}
          </button>
        </div>
      )}

      {open && <SaleDetailView saleId={open.saleId} folio={open.folio} onClose={() => setOpen(null)} />}
    </div>
  );
}
